import { AsyncPipe, DatePipe, NgIf } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  map,
  startWith,
} from 'rxjs';

import { MitsFormHeaderComponent } from 'src/packages/shared-ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { MitsDataGridComponent } from 'src/app/components/mits-data-grid/mits-data-grid.component';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { MitsDataGridColumnConfig } from 'src/app/components/mits-data-grid/model';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { OrderByParam } from 'src/app/models/order-by-param';
import { DAMAGE_REPORTING_BASE_ROUTE } from 'src/packages/damage-reporting/damage-report-management.routes';
import { DamageReportShowModel } from 'src/packages/damage-reporting/data/models/_show/damage-report.show-model';
import { DamageListItemStateEnum } from 'src/packages/damage-reporting/data/models/_view/damage-list-item-state.enum';
import { DamageListItemModel } from 'src/packages/damage-reporting/data/models/_view/damage-list-item.model';
import { DamageReportMapperService } from 'src/packages/damage-reporting/data/models/_view/damage-report-show-model-to-view-model.mapper';
import { DamageReportOfflineDataService } from 'src/packages/damage-reporting/data/services/damage-report.offline-data.service';
import { DamageStatePipe } from 'src/packages/damage-reporting/pipes/damage-state.pipe';
import { DamageWeightPipe } from 'src/packages/damage-reporting/pipes/damage-weight.pipe';

/**
 * Displays a list of damage reports for a particular machine.
 * Allows filtering by damage state, paging, and creating new damage reports.
 */
@Component({
  selector: 'damage-reporting-damage-list-for-machine-or-customer',
  templateUrl: './damage-list-for-machine-or-customer.component.html',
  styleUrls: ['./damage-list-for-machine-or-customer.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    MitsFormHeaderComponent,
    MitsDataGridComponent,
    ReactiveFormsModule,
    NgIf,
    AsyncPipe,
  ],
  providers: [DamageReportMapperService, DamageStatePipe, DamageWeightPipe],
})
export class DamageListForMachineOrCustomerComponent implements OnInit {
  // ─────────────────────────────────────────────────────────────────────────────
  // Component Inputs for UI configuration
  // ─────────────────────────────────────────────────────────────────────────────

  @Input() title = $localize`Schäden`;
  @Input() description = ``;
  @Input() showAdd = false;
  @Input() collapsed = false;
  @Input() mode: 'customer' | 'machine' = 'machine';

  /**
   * The ID of the machine for which damage reports are loaded.
   * Whenever machineId changes, we fetch new data.
   */
  @Input()
  set machineId(value: number | undefined) {
    this._machineID = value;
    if (value) {
      // Trigger fetch of new damage reports whenever the machine ID changes
      this.loadDamagesForMachineOrCustomer(value);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Dependencies injected via DI
  // ─────────────────────────────────────────────────────────────────────────────

  private readonly router = inject(Router);
  private readonly damageReportOfflineDataService = inject(
    DamageReportOfflineDataService,
  );
  private readonly damageReportMapperService = inject(
    DamageReportMapperService,
  );

  protected readonly DamageListItemStateEnum = DamageListItemStateEnum;
  private readonly datePipe = inject(DatePipe);

  // ─────────────────────────────────────────────────────────────────────────────
  // Internal State / RxJS Streams
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * The ID of the machine for which we are currently loading damage reports.
   * @private
   */
  private _machineID?: number;

  /**
   * A BehaviorSubject that holds all **raw** damage reports for the current machine.
   * Only updated when `machineId` changes.
   */
  private _damageListItems$ = new BehaviorSubject<DamageListItemModel[]>([]);
  public allDamageReports$ = this._damageListItems$.asObservable();

  /**
   * Form controls for filtering and sorting.
   */
  segmentControl = new FormControl<DamageListItemStateEnum>(
    DamageListItemStateEnum.OPEN,
    { nonNullable: true },
  );
  orderByControl = new FormControl<OrderByParam>(
    { attribute: 'created_time', direction: 'ASC' },
    { nonNullable: true },
  );
  searchControl = new FormControl<string>('');

  /**
   * Compute the total damage counts for **all** loaded reports
   * (irrespective of filtering in segmentControl).
   * This is useful for disabling segment buttons that have zero entries, etc.
   */
  damageReportCounts$ = this.allDamageReports$.pipe(
    map(allReports => this.buildDamageReportCounts(allReports)),
  );

  /**
   * A combined stream that applies filtering (by segmentControl)
   * and sorting (by orderByControl) **on the client side** to
   * the already-loaded `allDamageReports$`.
   */
  filteredSortedDamageReports$ = combineLatest([
    this.allDamageReports$,
    this.segmentControl.valueChanges.pipe(startWith(this.segmentControl.value)),
    this.orderByControl.valueChanges.pipe(startWith(this.orderByControl.value)),
    this.searchControl.valueChanges.pipe(startWith(this.searchControl.value)),
  ]).pipe(
    map(([allReports, selectedState, orderBy]) => {
      // 1) Filter by damage state
      const filtered = this.filterReportsByState(allReports, selectedState);
      // 2) Sort them by the current orderBy setting
      const sorted = this.sortDamageReports(filtered, orderBy);
      // 3) Apply search filter to search for damage-titles
      const search = this.searchControl.value?.toLowerCase();
      if (search) {
        return sorted.filter(r => r.title.toLowerCase().includes(search));
      }

      return sorted;
    }),
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Table Configuration
  // ─────────────────────────────────────────────────────────────────────────────

  readonly columns: MitsDataGridColumnConfig<DamageListItemModel>[] = [
    {
      attribute: 'title',
      label: $localize`Titel`,
    },
    {
      attribute: 'feature',
      label: $localize`Anbauteil (SN)`,
    },
    {
      attribute: 'created_by',
      label: $localize`Erfasst von`,
    },
    {
      attribute: 'changed_time',
      label: $localize`Zuletzt aktualisiert am/um`,
      transformValue: value =>
        this.datePipe.transform(value, 'dd.MM.yyyy HH:mm') ?? '',
    },
    {
      attribute: 'created_time',
      label: $localize`Erfasst am/um`,
      transformValue: value =>
        this.datePipe.transform(value, 'dd.MM.yyyy HH:mm') ?? '',
    },
    {
      attribute: 'state_text',
      label: $localize`Status`,
    },
  ];

  /**
   * Initializes the component.
   * Adds a new column for 'machine' between 'title' and 'feature' if the mode is 'customer'.
   */
  ngOnInit(): void {
    if (this.mode === 'customer') {
      this.columns.splice(1, 0, {
        attribute: 'machine',
        label: $localize`Maschine`,
      });
    }
  }

  /**
   * Public Method to reload Data from from parent component since this component has no ionic lifecycle hooks.
   */
  public reload(): void {
    if (this._machineID) this.loadDamagesForMachineOrCustomer(this._machineID);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Public UI Event Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  async navigateToExistingDamageReport(damageReportId: number): Promise<void> {
    await this.router.navigate([
      DAMAGE_REPORTING_BASE_ROUTE,
      'damage-reports',
      'details',
      damageReportId,
    ]);
  }

  async navigateToNewDamageReport(): Promise<void> {
    if (!this._machineID) return;
    await this.router.navigate(
      [DAMAGE_REPORTING_BASE_ROUTE, 'damage-reports', 'create'],
      {
        queryParams: { machineId: this._machineID },
      },
    );
  }

  collapse(): void {
    this.collapsed = !this.collapsed;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Private Helper Methods
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Loads all damage reports for a given machine or customer.
   */
  private async loadDamagesForMachineOrCustomer(
    machineOrCustomerId: number,
  ): Promise<void> {
    try {
      // 1. Fetch all damage reports for the machine OR the Customer from offline storage
      let damageReportsForMachineFromOfflineData: DamageReportShowModel[];
      if (this.mode === 'customer') {
        damageReportsForMachineFromOfflineData = await firstValueFrom(
          this.damageReportOfflineDataService.allByCustomerId(
            machineOrCustomerId,
          ),
        );
      } else {
        damageReportsForMachineFromOfflineData = await firstValueFrom(
          this.damageReportOfflineDataService.allByMachineId(
            machineOrCustomerId,
          ),
        );
      }

      // 2. Map the raw data to a view model that the template can display
      const damagesForMachineToDisplay: DamageListItemModel[] =
        this.damageReportMapperService.map(
          damageReportsForMachineFromOfflineData,
        );

      // 3. Push data into BehaviorSubject (for the rest of the pipeline)
      this._damageListItems$.next(damagesForMachineToDisplay);
    } catch (error) {
      console.error(
        '[DamageListForMachineOrCustomerComponent][loadDamagesForMachine]',
        error,
      );
      this._damageListItems$.next([]);
    }
  }

  /**
   * Builds a map of total counts per damage state from the raw list of reports.
   * Used by the template to enable/disable segment buttons.
   */
  private buildDamageReportCounts(
    allReports: DamageListItemModel[],
  ): Map<DamageListItemStateEnum, number> {
    const newMap = new Map<DamageListItemStateEnum, number>();

    for (const report of allReports) {
      const oldCount = newMap.get(report.state) ?? 0;
      newMap.set(report.state, oldCount + 1);
    }

    return newMap;
  }

  /**
   * Returns a filtered array of DamageListItemModel by a given state.
   * If the state is "ALL", no filtering is applied.
   */
  private filterReportsByState(
    reports: DamageListItemModel[],
    selectedState: DamageListItemStateEnum,
  ): DamageListItemModel[] {
    // Convert to number if needed
    selectedState = +selectedState;

    if (selectedState === DamageListItemStateEnum.ALL) {
      return reports;
    }

    return reports.filter(r => r.state === selectedState);
  }

  /**
   * Simple sort logic based on the current orderBy control. Extend as needed.
   */
  private sortDamageReports(
    reports: DamageListItemModel[],
    orderBy: OrderByParam,
  ): DamageListItemModel[] {
    const { attribute, direction } = orderBy;

    // For example: sorting by "state" or "created_at" or any other attribute
    // This is just a minimal example. Extend for your real use-cases.
    return [...reports].sort((a, b) => {
      const valA = (a as any)[attribute];
      const valB = (b as any)[attribute];

      // Basic ascending/descending comparison
      if (valA < valB) return direction === 'ASC' ? -1 : 1;
      if (valA > valB) return direction === 'ASC' ? 1 : -1;
      return 0;
    });
  }
}
